import React from 'react';
import { FIELD_OF_WORK_OPTIONS } from '../utils';
import { sitesForTenant } from '../utils/sites';
import { useTenants } from '../contexts/TenantContext';
import type { ItemVerificationStatus } from '../types';

const STAGES: { value: ItemVerificationStatus | ''; label: string }[] = [
  { value: '', label: 'Needs verification' },
  { value: 'self_reported', label: 'Self-reported' },
  { value: 'evidence_supplied', label: 'Evidence supplied' },
  { value: 'manually_confirmed', label: 'Accepted by Employer' },
  { value: 'verified', label: 'Verified' },
];

interface DirectoryFilterBarProps {
  search: string;
  onSearch: (value: string) => void;
  fieldFilter: string;
  onFieldFilter: (value: string) => void;
  tenantId?: string;
  onTenantId?: (value: string) => void;
  siteId: string;
  onSiteId: (value: string) => void;
  showTenant?: boolean;
  stage?: ItemVerificationStatus | '';
  onStage?: (value: ItemVerificationStatus | '') => void;
  groupBySite?: boolean;
  onGroupBySite?: (value: boolean) => void;
  hideField?: boolean;
  resultLabel?: string;
  onClear: () => void;
  hasActiveFilters: boolean;
}

export const DirectoryFilterBar: React.FC<DirectoryFilterBarProps> = ({
  search,
  onSearch,
  fieldFilter,
  onFieldFilter,
  tenantId = '',
  onTenantId,
  siteId,
  onSiteId,
  showTenant = false,
  stage,
  onStage,
  groupBySite,
  onGroupBySite,
  hideField = false,
  resultLabel,
  onClear,
  hasActiveFilters,
}) => {
  const { tenants } = useTenants();
  const siteOptions = sitesForTenant(tenants, tenantId || undefined);
  const siteDisabled = showTenant && !tenantId;

  return (
    <div className="space-y-3">
      <div className="flex flex-col lg:flex-row gap-3">
        {showTenant && onTenantId && (
          <select
            value={tenantId}
            onChange={e => {
              onTenantId(e.target.value);
              onSiteId('');
            }}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white lg:w-56"
          >
            <option value="">All my tenants</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
            ))}
          </select>
        )}
        <select
          value={siteId}
          onChange={e => onSiteId(e.target.value)}
          disabled={siteDisabled}
          className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white lg:w-48 disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">
            {siteDisabled ? 'Select a tenant first' : 'All sites'}
          </option>
          {siteOptions.map(site => (
            <option key={site.tenantSiteId || site.id} value={site.tenantSiteId || site.id}>
              {site.name}
            </option>
          ))}
        </select>
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={e => onSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        {hideField ? null : (
        <select
          value={fieldFilter}
          onChange={e => onFieldFilter(e.target.value)}
          className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          <option value="">All fields of work</option>
          {FIELD_OF_WORK_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        )}
        {onStage && (
          <select
            value={stage ?? ''}
            onChange={e => onStage(e.target.value as ItemVerificationStatus | '')}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          >
            {STAGES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {onGroupBySite ? (
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={Boolean(groupBySite)}
              onChange={e => onGroupBySite(e.target.checked)}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Group by site
          </label>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3 text-sm">
          {resultLabel && <span className="text-gray-500">{resultLabel}</span>}
          {hasActiveFilters && (
            <button type="button" onClick={onClear} className="text-brand-600 hover:text-brand-800 font-medium">
              Clear filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
